import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReviewModal from '../../layouts/ReviewModal';
import NoteApplyingSkeleton from '../../components/review/NoteApplyingSkeleton';
import { REVIEW_PAGE_BG } from '../../components/review/reviewStyles';
import { useLanguage } from '../../contexts/LanguageContext';
import { useHoldToRecord } from '../../contexts/HoldToRecordContext';
import { doctorPatientPath, doctorReviewPath, ROUTES } from '../../constants/routes';
import { generateDoctorNote } from '../services/doctorNotePipeline';
import { doctorPatientNotes } from '../services/doctorApi';
import { useDoctorSession } from '../contexts/DoctorSessionContext';

export default function DoctorGeneratingScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { showOverlayError } = useHoldToRecord();
  const { draftNote, setDraftNote, trialExhausted } = useDoctorSession();
  const [error, setError] = useState('');
  const [retryToken, setRetryToken] = useState(0);
  const ran = useRef(false);

  useEffect(() => {
    if (trialExhausted) {
      navigate(ROUTES.DOCTOR_PAYWALL, { replace: true });
      return undefined;
    }
    if (!draftNote?.transcript || draftNote.patientId !== id) {
      // After a no-clinical redirect we clear the draft; don't steal navigation.
      if (ran.current) return undefined;
      navigate(doctorPatientPath(id), { replace: true });
      return undefined;
    }
    if (ran.current) return undefined;
    ran.current = true;

    const transcript = draftNote.transcript;
    const patient = draftNote.patient;
    const audioDurationSeconds = draftNote.audioDurationSeconds;

    (async () => {
      try {
        let previousNotes = [];
        try {
          const history = await doctorPatientNotes(id, {
            status: 'approved',
            limit: 3,
          });
          previousNotes = history.notes || [];
        } catch {
          previousNotes = [];
        }

        const result = await generateDoctorNote(transcript, {
          allergies: patient?.allergies || '',
          previousNotes,
        });
        setDraftNote({
          patientId: id,
          patient,
          transcript,
          audioDurationSeconds,
          ...result,
        });
        navigate(doctorReviewPath(id), { replace: true });
      } catch (err) {
        if (err?.code === 'no_clinical_content' || err?.message === 'no_clinical_content') {
          const patientLabel = patient?.full_name || null;
          setDraftNote(null);
          navigate(doctorPatientPath(id), { replace: true });
          showOverlayError(t('noClinicalContent'), {
            patientId: id,
            patientLabel,
          });
          return;
        }
        setError(err.message || t('doctorGenerateFailed'));
      }
    })();
    return undefined;
  }, [
    draftNote,
    id,
    navigate,
    setDraftNote,
    showOverlayError,
    t,
    trialExhausted,
    retryToken,
  ]);

  const handleRetry = () => {
    setError('');
    ran.current = false;
    setRetryToken((value) => value + 1);
  };

  return (
    <ReviewModal forceEnglish onClose={() => navigate(doctorPatientPath(id))}>
      <div className={`flex h-full min-h-0 flex-col overflow-hidden ${REVIEW_PAGE_BG}`}>
        {error ? (
          <div className="flex flex-1 items-center justify-center px-4">
            <div className="w-full max-w-md rounded-xl border border-critical bg-critical-light px-4 py-3 text-sm text-critical-dark">
              <p>{error}</p>
              <button
                type="button"
                onClick={handleRetry}
                className="mt-3 rounded-lg bg-critical px-4 py-2 text-sm font-semibold text-white"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-4">
            <NoteApplyingSkeleton />
          </div>
        )}
      </div>
    </ReviewModal>
  );
}
